import type { ErrorCode } from '@baex/shared';

/**
 * The single HTTP entry point for the admin UI.
 *
 * Responsibilities:
 *  - double-submit CSRF: mirrors the non-HttpOnly `baex_csrf` cookie into `X-CSRF-Token`
 *    on every non-GET request (functional spec, API Contract Reference — Admin API);
 *  - unwraps `{ error: { code, message, requestId } }` into a typed {@link ApiError};
 *  - turns a 401 into a redirect to the login screen (except on the login call itself).
 */

export const CSRF_COOKIE = 'baex_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly requestId: string | null;

  constructor(params: {
    status: number;
    code: ErrorCode | 'NETWORK_ERROR';
    message: string;
    requestId: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.requestId = params.requestId;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

/** Where an unauthenticated response should send the browser. */
export const LOGIN_PATH = '/admin/login';

let onUnauthorized: (() => void) | null = null;

/** The router installs a handler so a 401 navigates instead of hard-reloading. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function handleUnauthorized(): void {
  if (onUnauthorized) {
    onUnauthorized();
    return;
  }
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

/** `fetch` rejects with an `AbortError` when its signal is aborted — in every runtime. */
function isAbort(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError';
}

export type QueryValue = string | number | boolean | null | undefined;

/**
 * Serialises query parameters. Only `undefined` and `null` are dropped: an empty string is
 * a meaningful value for `subfolder`, where `subfolder=` means "the folder root" and an
 * absent parameter means "no subfolder filter" (functional spec, API Contract Reference — Transfer history). Callers that mean
 * "omit" must pass `undefined`, never `''`.
 */
export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Login must surface its own 401 instead of redirecting to itself. */
  skipAuthRedirect?: boolean;
  signal?: AbortSignal;
}

function parseErrorBody(
  payload: unknown,
  status: number,
): {
  code: ErrorCode | 'NETWORK_ERROR';
  message: string;
  requestId: string | null;
} {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const envelope = (payload as { error: unknown }).error;
    if (envelope && typeof envelope === 'object') {
      const record = envelope as Record<string, unknown>;
      return {
        code: (typeof record.code === 'string' ? record.code : 'INTERNAL_ERROR') as ErrorCode,
        message:
          typeof record.message === 'string' && record.message.length > 0
            ? record.message
            : `Request failed (HTTP ${status}).`,
        requestId: typeof record.requestId === 'string' ? record.requestId : null,
      };
    }
  }
  return {
    code: 'INTERNAL_ERROR' as ErrorCode,
    message: `Request failed (HTTP ${status}).`,
    requestId: null,
  };
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (method !== 'GET') {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers[CSRF_HEADER] = token;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    /*
     * A cancelled request is not a failed one. React Query aborts the in-flight fetch
     * whenever the query key changes, which on the Transfers screen is every keystroke in
     * Search and every change of a filter — and each of those aborts was being dressed up
     * as "The server could not be reached. Check your connection and try again." in a red
     * banner above the table. Nothing was wrong, and the one person who could tell was
     * being sent to look at the network.
     *
     * Rethrown untouched so React Query recognises its own cancellation and surfaces
     * nothing at all.
     */
    if (isAbort(cause) || options.signal?.aborted === true) throw cause;
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'The server could not be reached. Check your connection and try again.',
      requestId: null,
    });
  }

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  let payload: unknown = null;
  if (raw.length > 0) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && !options.skipAuthRedirect) handleUnauthorized();
    const parsed = parseErrorBody(payload, response.status);
    throw new ApiError({ status: response.status, ...parsed });
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', ...(body !== undefined ? { body } : {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', ...(body !== undefined ? { body } : {}) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', ...(body !== undefined ? { body } : {}) }),
};
